import {
  ClientSettingsSchema,
  EnvironmentId,
  MAX_APP_FONT_SIZE,
  MIN_APP_FONT_SIZE,
  normalizePersistedSavedEnvironmentRecord,
  type ClientSettings,
  type EnvironmentId as EnvironmentIdValue,
  type PersistedSavedEnvironmentRecord,
} from "@cmmd-forge/contracts";
import * as Schema from "effect/Schema";

import { getLocalStorageItem, setLocalStorageItem } from "./hooks/useLocalStorage";
import { classifyEnvironmentId } from "./environments/environmentIdClassification";

export const CLIENT_SETTINGS_STORAGE_KEY = "forge:client-settings:v1";
export const SAVED_ENVIRONMENT_REGISTRY_STORAGE_KEY = "forge:saved-environment-registry:v1";

const BrowserSavedEnvironmentRecordSchema = Schema.Struct({
  environmentId: EnvironmentId,
  label: Schema.String,
  httpBaseUrl: Schema.String,
  wsBaseUrl: Schema.String,
  createdAt: Schema.String,
  lastConnectedAt: Schema.NullOr(Schema.String),
  cmmdOrganizationId: Schema.optionalKey(Schema.String),
  cmmdProjectId: Schema.optionalKey(Schema.String),
  runtimeId: Schema.optionalKey(Schema.String),
  runtimeTicketExpiresAt: Schema.optionalKey(Schema.String),
  lastAuthenticatedPullRequestCapability: Schema.optionalKey(Schema.Boolean),
  bearerToken: Schema.optionalKey(Schema.String),
});
type BrowserSavedEnvironmentRecord = typeof BrowserSavedEnvironmentRecordSchema.Type;

const BrowserSavedEnvironmentRegistryDocumentSchema = Schema.Struct({
  version: Schema.optionalKey(Schema.Number),
  records: Schema.optionalKey(Schema.Array(BrowserSavedEnvironmentRecordSchema)),
});
type BrowserSavedEnvironmentRegistryDocument =
  typeof BrowserSavedEnvironmentRegistryDocumentSchema.Type;

function hasWindow(): boolean {
  return typeof window !== "undefined";
}

/**
 * Font defaults that were current before `FONT_DEFAULTS_VERSION` 2, keyed by
 * the setting they belong to.
 *
 * A stored value equal to one of these, on a payload with no version marker,
 * is a *frozen default* rather than a choice — see `FONT_DEFAULTS_VERSION` for
 * why storage cannot tell them apart on its own. Dropping the key lets the
 * schema's decoding default supply the current value instead.
 *
 * Each size is judged on its own: someone who moved the UI size but never
 * touched the code size should still be lifted onto the new code default.
 */
const SUPERSEDED_FONT_DEFAULTS: Readonly<Record<string, number>> = {
  uiFontSize: 14,
  codeFontSize: 15,
};

/**
 * Numeric settings whose schema constrains them to a range, with that range.
 *
 * These are clamped before decode rather than left to fail it. The reason is
 * blunt: `withDecodingDefault` supplies a value for an **absent** key and does
 * nothing for a present-but-invalid one, which throws — and the throw is caught
 * in `readBrowserClientSettings`, which returns `null`, which the caller reads
 * as "nothing stored" and replaces with defaults. One out-of-range number
 * therefore discards *every setting the user has ever changed*.
 *
 * Clamping keeps the blast radius on the offending field. Genuine garbage (a
 * string, a non-object payload) still fails decode and still falls back, so
 * this does not turn the reader into something that accepts anything.
 */
const CLAMPED_NUMERIC_SETTINGS: ReadonlyArray<
  readonly [key: string, minimum: number, maximum: number]
> = [
  ["uiFontSize", MIN_APP_FONT_SIZE, MAX_APP_FONT_SIZE],
  ["codeFontSize", MIN_APP_FONT_SIZE, MAX_APP_FONT_SIZE],
];

/**
 * Rewrites a stored payload before it is decoded.
 *
 * Exported because the pre-paint appearance read in `useTheme` parses this key
 * itself, without the schema. If the two disagree, first paint uses the old
 * size and React resizes the whole app a frame later.
 */
/** The renamed runtime value, from before `cloud` was the name. */
function migrateThreadRuntime(input: Record<string, unknown>): Record<string, unknown> | null {
  if (input.defaultThreadRuntime !== "desktop-cloud-bridge") return null;
  return { ...input, defaultThreadRuntime: "cloud" };
}

/** Drops a font size that is a frozen superseded default. See `FONT_DEFAULTS_VERSION`. */
function dropSupersededFontDefaults(
  input: Record<string, unknown>,
): Record<string, unknown> | null {
  if (typeof input.fontDefaultsVersion === "number") return null;
  const stale = Object.entries(SUPERSEDED_FONT_DEFAULTS).filter(
    ([key, superseded]) => input[key] === superseded,
  );
  if (stale.length === 0) return null;
  const next = { ...input };
  for (const [key] of stale) delete next[key];
  return next;
}

/** Pulls an out-of-range number back into range. See `CLAMPED_NUMERIC_SETTINGS`. */
function clampNumericSettings(input: Record<string, unknown>): Record<string, unknown> | null {
  const fixes = CLAMPED_NUMERIC_SETTINGS.flatMap(([key, minimum, maximum]) => {
    const stored = input[key];
    if (typeof stored !== "number" || !Number.isFinite(stored)) return [];
    const clamped = Math.min(maximum, Math.max(minimum, Math.round(stored)));
    return clamped === stored ? [] : [[key, clamped] as const];
  });
  if (fixes.length === 0) return null;
  const next = { ...input };
  for (const [key, clamped] of fixes) next[key] = clamped;
  return next;
}

/**
 * Rewrites a stored payload before it is decoded.
 *
 * Exported because the pre-paint appearance read in `useTheme` parses this key
 * itself, without the schema. If the two disagree, first paint uses the old
 * size and React resizes the whole app a frame later.
 *
 * Each migration is its own function and returns null for "nothing to do", so
 * adding the next one does not make this body harder to read — which is what
 * happened when the clamp was inlined alongside the other two.
 */
export function normalizeClientSettingsInput(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;

  let next = value as Record<string, unknown>;
  let changed = false;
  for (const migrate of [migrateThreadRuntime, dropSupersededFontDefaults, clampNumericSettings]) {
    const result = migrate(next);
    if (result === null) continue;
    next = result;
    changed = true;
  }
  return changed ? next : value;
}

export function readBrowserClientSettings(): ClientSettings | null {
  if (!hasWindow()) {
    return null;
  }

  try {
    const item = window.localStorage.getItem(CLIENT_SETTINGS_STORAGE_KEY);
    if (!item) {
      return null;
    }
    return Schema.decodeUnknownSync(ClientSettingsSchema)(
      normalizeClientSettingsInput(JSON.parse(item)),
    );
  } catch {
    return null;
  }
}

export function writeBrowserClientSettings(settings: ClientSettings): void {
  if (!hasWindow()) {
    return;
  }

  setLocalStorageItem(CLIENT_SETTINGS_STORAGE_KEY, settings, ClientSettingsSchema);
}

function readBrowserSavedEnvironmentRegistryDocument(): BrowserSavedEnvironmentRegistryDocument {
  if (!hasWindow()) {
    return {};
  }

  try {
    const parsed = getLocalStorageItem(
      SAVED_ENVIRONMENT_REGISTRY_STORAGE_KEY,
      BrowserSavedEnvironmentRegistryDocumentSchema,
    );
    return parsed ?? {};
  } catch {
    return {};
  }
}

function writeBrowserSavedEnvironmentRegistryDocument(
  document: BrowserSavedEnvironmentRegistryDocument,
): void {
  if (!hasWindow()) {
    return;
  }

  setLocalStorageItem(
    SAVED_ENVIRONMENT_REGISTRY_STORAGE_KEY,
    document,
    BrowserSavedEnvironmentRegistryDocumentSchema,
  );
}

function readBrowserSavedEnvironmentRecordsWithSecrets(): ReadonlyArray<BrowserSavedEnvironmentRecord> {
  return readBrowserSavedEnvironmentRegistryDocument().records ?? [];
}

function writeBrowserSavedEnvironmentRecords(
  records: ReadonlyArray<BrowserSavedEnvironmentRecord>,
): void {
  writeBrowserSavedEnvironmentRegistryDocument({
    version: 1,
    records,
  });
}

export function readBrowserSavedEnvironmentRegistry(): ReadonlyArray<PersistedSavedEnvironmentRecord> {
  return readBrowserSavedEnvironmentRecordsWithSecrets().map((record) =>
    normalizePersistedSavedEnvironmentRecord(record),
  );
}

export function writeBrowserSavedEnvironmentRegistry(
  records: ReadonlyArray<PersistedSavedEnvironmentRecord>,
): void {
  const existing = new Map(
    readBrowserSavedEnvironmentRecordsWithSecrets().map(
      (record) => [record.environmentId, record] as const,
    ),
  );
  writeBrowserSavedEnvironmentRecords(
    records.map((record) => {
      // Deliberate, not a gap: a legacy `cmmd-runtime:` id is documented as
      // unstable and drops its cached bearer token on every write, while a
      // durable `env_` id is documented as stable and keeps it. See
      // `docs/specs/2026-09-16-environment-id-classification-spec.md`.
      const bearerToken =
        classifyEnvironmentId(record.environmentId).legacyRuntimeId !== null
          ? undefined
          : existing.get(record.environmentId)?.bearerToken;
      return bearerToken
        ? { ...normalizePersistedSavedEnvironmentRecord(record), bearerToken }
        : normalizePersistedSavedEnvironmentRecord(record);
    }),
  );
}

export function readBrowserSavedEnvironmentSecret(
  environmentId: EnvironmentIdValue,
): string | null {
  return (
    readBrowserSavedEnvironmentRecordsWithSecrets().find(
      (record) => record.environmentId === environmentId,
    )?.bearerToken ?? null
  );
}

export function writeBrowserSavedEnvironmentSecret(
  environmentId: EnvironmentIdValue,
  secret: string,
): boolean {
  const document = readBrowserSavedEnvironmentRegistryDocument();
  const records = document.records ?? [];
  let found = false;
  writeBrowserSavedEnvironmentRegistryDocument({
    version: document.version ?? 1,
    records: records.map((record) => {
      if (record.environmentId !== environmentId) {
        return record;
      }
      found = true;
      return Object.assign(normalizePersistedSavedEnvironmentRecord(record), {
        bearerToken: secret,
      }) satisfies BrowserSavedEnvironmentRecord;
    }),
  });
  return found;
}

export function removeBrowserSavedEnvironmentSecret(environmentId: EnvironmentIdValue): void {
  const document = readBrowserSavedEnvironmentRegistryDocument();
  writeBrowserSavedEnvironmentRegistryDocument({
    version: document.version ?? 1,
    records: (document.records ?? []).map((record) => {
      if (record.environmentId !== environmentId) {
        return record;
      }
      return normalizePersistedSavedEnvironmentRecord(record);
    }),
  });
}
